import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeartPulse,
  faRotateRight,
  faCircleCheck,
  faTriangleExclamation,
  faCircleXmark,
  faCircleQuestion,
} from "@fortawesome/free-solid-svg-icons";
import { useSystemHealth } from "../../hooks/admin/useSystemHealth";

const STATUS_META = {
  ok: {
    label: "Operational",
    dot: "bg-green-500",
    text: "text-green-700",
    chip: "bg-green-50 border-green-200 text-green-700",
    icon: faCircleCheck,
  },
  degraded: {
    label: "Degraded",
    dot: "bg-amber-500",
    text: "text-amber-700",
    chip: "bg-amber-50 border-amber-200 text-amber-700",
    icon: faTriangleExclamation,
  },
  down: {
    label: "Down",
    dot: "bg-red-500",
    text: "text-red-700",
    chip: "bg-red-50 border-red-200 text-red-700",
    icon: faCircleXmark,
  },
  unknown: {
    label: "Unknown",
    dot: "bg-gray-400",
    text: "text-gray-600",
    chip: "bg-gray-50 border-gray-200 text-gray-600",
    icon: faCircleQuestion,
  },
};

const meta = (status) => STATUS_META[status] || STATUS_META.unknown;

const SystemHealth = () => {
  const { data, isLoading, isError, isFetching, refetch, dataUpdatedAt } =
    useSystemHealth();

  const overall = meta(data?.status);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="icon-tile">
            <FontAwesomeIcon icon={faHeartPulse} className="text-deep-red" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-bold text-dark-grey">
              System Health
            </h2>
            {data && (
              <div className="flex items-center gap-2 text-sm">
                <span className={`w-2.5 h-2.5 rounded-full ${overall.dot}`} />
                <span className={`font-semibold ${overall.text}`}>
                  {overall.label}
                </span>
                {dataUpdatedAt > 0 && (
                  <span className="text-medium-grey">
                    · updated{" "}
                    {new Date(dataUpdatedAt).toLocaleTimeString()}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="btn btn--secondary text-sm self-start sm:self-auto"
        >
          <FontAwesomeIcon
            icon={faRotateRight}
            className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
          />
          {isFetching ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="text-medium-grey text-sm py-6 text-center">
          Loading service status...
        </div>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">
          Failed to load system health. You may not have admin access, or the
          API is unreachable.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(data?.services || []).map((svc) => {
            const m = meta(svc.status);
            return (
              <div
                key={svc.name}
                className="flex items-start justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-dark-grey text-sm">
                    {svc.name}
                  </div>
                  <div className="text-xs text-medium-grey break-words">
                    {svc.detail}
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${m.chip}`}
                >
                  <FontAwesomeIcon icon={m.icon} className="w-3 h-3" />
                  {m.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SystemHealth;
